import React, { useState } from 'react';
import { User, Palette, Image, Plus, Edit3, Save, CheckCircle } from 'lucide-react';
import { AssistantFormData, PersonalContextEntry } from '../../types';
import { PRONOUN_OPTIONS } from '../../constants';
import { getCurrentUrlPrefix } from '../../utils/helpers';
import ImageUpload from './ImageUpload';
import ContextModal from './ContextModal';

interface BuildYourStoryPageProps {
    formData: AssistantFormData;
    personalContextEntries: PersonalContextEntry[];
    backgroundImageUrl: string | undefined;
    imageUploadLoading: boolean;
    editLoading: boolean;
    showEditModal: boolean;
    isAddingNew: boolean;
    editingTitle: string;
    tempValue: string;
    currentPrompt: string;
    onFormDataChange: (data: Partial<AssistantFormData>) => void;
    onSaveBasicInfo: () => Promise<void>;
    onSaveColor: (color: string) => void;
    onImageUpload: (file: File) => Promise<void>;
    onImageRemove: () => Promise<void>;
    onAddNew: () => void;
    onEditContext: (entryId: string, title: string, content: string, prompt?: string, description?: string) => void;
    onSaveContext: () => void;
    onCancelModal: () => void;
    onTitleChange: (title: string) => void;
    onContentChange: (content: string) => void;
    getCharacterCountColor: (current: number, max: number) => string;
    isFieldValid?: (field: string, value: string) => boolean;
}

const BuildYourStoryPage: React.FC<BuildYourStoryPageProps> = ({
    formData,
    personalContextEntries,
    backgroundImageUrl,
    imageUploadLoading,
    editLoading,
    showEditModal,
    isAddingNew,
    editingTitle,
    tempValue,
    currentPrompt,
    onFormDataChange,
    onSaveBasicInfo,
    onSaveColor,
    onImageUpload,
    onImageRemove,
    onAddNew,
    onEditContext,
    onSaveContext,
    onCancelModal,
    onTitleChange,
    onContentChange,
    getCharacterCountColor,
    isFieldValid
}) => {
    const [activeSection, setActiveSection] = useState<'identity' | 'story' | 'style'>('identity');

    const completionStatus = {
        identity: formData.name && formData.pronouns,
        story: personalContextEntries.length > 0,
        style: backgroundImageUrl || formData.primary_color !== '#3B82F6'
    };

    const sections = [
        {
            id: 'identity' as const,
            title: 'Your Identity',
            description: 'Basic information about who you are',
            icon: User,
            completed: completionStatus.identity
        },
        {
            id: 'story' as const,
            title: 'Your Story',
            description: 'Share your background, experience, and what makes you unique',
            icon: Edit3,
            completed: completionStatus.story
        },
        {
            id: 'style' as const,
            title: 'Your Style',
            description: 'Customize how your Echoo looks and feels',
            icon: Palette,
            completed: completionStatus.style
        }
    ];

    const SectionHeader = ({ section }: { section: typeof sections[0] }) => (
        <button
            onClick={() => setActiveSection(section.id)}
            className={`w-full flex items-center p-4 rounded-xl text-left transition-all ${activeSection === section.id
                    ? 'bg-blue-50 border-2 border-blue-200'
                    : 'bg-white border-2 border-slate-200 hover:border-slate-300'
                }`}
        >
            <div className={`p-2 rounded-lg mr-4 ${activeSection === section.id ? 'bg-blue-600' : 'bg-slate-100'
                }`}>
                <section.icon className={`w-5 h-5 ${activeSection === section.id ? 'text-white' : 'text-slate-600'
                    }`} />
            </div>
            <div className="flex-1">
                <div className="flex items-center">
                    <h3 className="text-lg font-semibold text-slate-900 mr-2">{section.title}</h3>
                    {section.completed && <CheckCircle className="w-5 h-5 text-green-500" />}
                </div>
                <p className="text-sm text-slate-600">{section.description}</p>
            </div>
        </button>
    );

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold text-slate-900 mb-2">Build Your Story</h1>
                <p className="text-slate-600">Help people get to know you by sharing your identity, story, and style</p>
            </div>

            {/* Progress Overview */}
            <div className="bg-white rounded-2xl p-6 border border-slate-200">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-slate-900">Your Progress</h2>
                    <div className="text-sm text-slate-600">
                        {Object.values(completionStatus).filter(Boolean).length} of 3 complete
                    </div>
                </div>
                <div className="grid gap-3">
                    {sections.map((section) => (
                        <SectionHeader key={section.id} section={section} />
                    ))}
                </div>
            </div>

            {/* Active Section Content */}
            {activeSection === 'identity' && (
                <div className="bg-white rounded-2xl p-6 border border-slate-200">
                    <h2 className="text-lg font-semibold text-slate-900 mb-4">Your Identity</h2>
                    <div className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                    Name *
                                </label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => onFormDataChange({ name: e.target.value })}
                                    className="w-full px-4 py-3 border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Your name"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                    Pronouns *
                                </label>
                                <select
                                    value={formData.pronouns}
                                    onChange={(e) => onFormDataChange({ pronouns: e.target.value })}
                                    className="w-full px-4 py-3 border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="">Select pronouns...</option>
                                    {PRONOUN_OPTIONS.map(option => (
                                        <option key={option} value={option}>{option}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                                URL Handle
                            </label>
                            <div className="flex items-center space-x-2">
                                <span className="text-sm text-slate-500">{getCurrentUrlPrefix()}</span>
                                <input
                                    type="text"
                                    value={formData.unique_url}
                                    onChange={(e) => onFormDataChange({ unique_url: e.target.value })}
                                    className="flex-1 px-4 py-3 border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="your-handle"
                                />
                            </div>
                            <p className="text-xs text-slate-500 mt-1">
                                This is your unique URL. Only lowercase letters, numbers, and hyphens are allowed.
                            </p>
                        </div>

                        <div className="flex justify-end">
                            <button
                                onClick={onSaveBasicInfo}
                                disabled={editLoading || (isFieldValid && (!isFieldValid('name', formData.name) || !isFieldValid('pronouns', formData.pronouns)))}
                                className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                <Save className="w-4 h-4 mr-2" />
                                {editLoading ? 'Saving...' : 'Save Identity'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {activeSection === 'story' && (
                <div className="bg-white rounded-2xl p-6 border border-slate-200">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h2 className="text-lg font-semibold text-slate-900">Your Story</h2>
                            <p className="text-sm text-slate-600 mt-1">Share the key things people should know about you</p>
                        </div>
                        <button
                            onClick={onAddNew}
                            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Add Section
                        </button>
                    </div>

                    <div className="space-y-4">
                        {personalContextEntries.length > 0 ? (
                            personalContextEntries.map((entry) => (
                                <div key={entry.id} className="border border-slate-200 rounded-lg p-4">
                                    <div className="flex items-start justify-between mb-3">
                                        <h3 className="text-md font-semibold text-slate-900">{entry.title}</h3>
                                        <button
                                            onClick={() => onEditContext(entry.id, entry.title, entry.content, entry.prompt, entry.description)}
                                            className="flex items-center px-2 py-1 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded transition-colors"
                                        >
                                            <Edit3 className="w-3 h-3 mr-1" />
                                            Edit
                                        </button>
                                    </div>
                                    <div className="text-slate-600 text-sm leading-relaxed">
                                        {entry.content ? (
                                            <span>{entry.content}</span>
                                        ) : entry.description ? (
                                            <span className="italic text-slate-500">{entry.description}</span>
                                        ) : (
                                            <span className="italic text-slate-400">No content yet</span>
                                        )}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-12 border-2 border-dashed border-slate-300 rounded-lg">
                                <Edit3 className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                                <h3 className="text-lg font-semibold text-slate-900 mb-2">No story sections yet</h3>
                                <p className="text-slate-600 mb-4">Add sections about your background, experience, and interests</p>
                                <button
                                    onClick={onAddNew}
                                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors mx-auto"
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    Add Your First Section
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {activeSection === 'style' && (
                <div className="bg-white rounded-2xl p-6 border border-slate-200">
                    <h2 className="text-lg font-semibold text-slate-900 mb-4">Your Style</h2>
                    <div className="space-y-8">
                        {/* Color Customization */}
                        <div>
                            <h3 className="text-md font-semibold text-slate-900 mb-3">Brand Color</h3>
                            <div className="flex items-center space-x-4">
                                <input
                                    type="color"
                                    value={formData.primary_color}
                                    onChange={(e) => onFormDataChange({ primary_color: e.target.value })}
                                    className="h-12 w-16 border border-slate-300 rounded-lg cursor-pointer"
                                />
                                <input
                                    type="text"
                                    value={formData.primary_color}
                                    onChange={(e) => onFormDataChange({ primary_color: e.target.value })}
                                    className="flex-1 px-4 py-3 border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="#3B82F6"
                                />
                                <button
                                    onClick={() => onSaveColor(formData.primary_color)}
                                    disabled={editLoading}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    {editLoading ? 'Saving...' : 'Save'}
                                </button>
                            </div>
                        </div>

                        {/* Background Image */}
                        <div>
                            <h3 className="text-md font-semibold text-slate-900 mb-3">Background Image</h3>
                            <p className="text-sm text-slate-600 mb-4">This image will appear as the background in your chat interface</p>
                            <ImageUpload
                                currentImageUrl={backgroundImageUrl}
                                onImageUpload={onImageUpload}
                                onImageRemove={onImageRemove}
                                loading={imageUploadLoading}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Context Modal */}
            <ContextModal
                isOpen={showEditModal}
                isAddingNew={isAddingNew}
                title={editingTitle}
                content={tempValue}
                prompt={currentPrompt}
                onTitleChange={onTitleChange}
                onContentChange={onContentChange}
                onSave={onSaveContext}
                onCancel={onCancelModal}
                isLoading={editLoading}
                getCharacterCountColor={getCharacterCountColor}
            />
        </div>
    );
};

export default BuildYourStoryPage;
